import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  mergeClasses,
  Radio,
  RadioGroup,
  Spinner,
  Text,
  Textarea,
  Tooltip,
} from "@fluentui/react-components";
import {
  ArrowEnterRegular,
  CheckmarkRegular,
  DismissRegular,
  Info16Regular,
  SparkleRegular,
} from "@fluentui/react-icons";
import { addObjectiveCreated } from "api";
import {
  addObjectiveToDiagnostic,
  generateObjectiveDescription,
  updateObjective,
} from "apps/diagnostics/api";
import {
  IObjective,
  IObjectiveQuestion,
  MAX_PREDEFINED_QUESTIONS,
  MIN_PREDEFINED_QUESTIONS,
  MAX_LIKERT_STATEMENTS,
  MIN_LIKERT_STATEMENTS,
  MAX_OBJECTIVE_QUESTION_LENGTH,
  MAX_LIKERT_SCALE_LABEL_LENGTH,
  OBJECTIVE_TYPE_AI_GENERATED,
  OBJECTIVE_TYPE_PREDEFINED,
  OBJECTIVE_TYPE_LIKERT_SCALE,
  OBJECTIVE_TYPE_LABEL_LIKERT_SCALE,
  DEFAULT_LIKERT_SCALE_LABELS,
  ObjectiveType,
  OBJECTIVE_TYPE_MULTIPLE_CHOICE,
  OBJECTIVE_TYPE_LABEL_MULTIPLE_CHOICE,
  MAX_MULTIPLE_CHOICE_QUESTIONS,
  MIN_MULTIPLE_CHOICE_QUESTIONS,
  MAX_MULTIPLE_CHOICE_OPTIONS,
  MIN_MULTIPLE_CHOICE_OPTIONS,
  MAX_MULTIPLE_CHOICE_OPTION_LENGTH,
} from "apps/diagnostics/models/IObjective";
import { ConstantValues } from "common/constants";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { ChangeEvent, useRef, useState } from "react";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
} from "theme/sharedStyles";
import { DialogTabs } from "components/dialogTabs/DialogTabs";
import { LabelWithInfo } from "../../labelWithInfo/LabelWithInfo";
import { useEditObjectiveStyles } from "./editObjective.styles";
import { IEditObjectiveProps } from "./editObjective.types";
import { AiGeneratedQuestionsForm } from "./AiGeneratedQuestionsForm";
import { PredefinedQuestionsForm } from "./PredefinedQuestionsForm";
import { LikertScaleForm } from "./LikertScaleForm";
import { CustomToggle } from "components/customToggle/CustomToggle";
import { MultipleChoiceForm } from "./MultipleChoiceForm";

export const EditObjective = (props: IEditObjectiveProps): JSX.Element => {
  const isEditMode = !!props.objective;
  const editStyles = useEditObjectiveStyles();
  const footerStyles = useSharedDialogFooterStyles();
  const dialogContentStyles = useSharedDialogContentStyles();

  const [objectiveType, setObjectiveType] = useState<ObjectiveType>(
    props.objective?.objectiveType || OBJECTIVE_TYPE_AI_GENERATED,
  );
  const isPredefined = objectiveType === OBJECTIVE_TYPE_PREDEFINED;
  const isLikert = objectiveType === OBJECTIVE_TYPE_LIKERT_SCALE;
  const isAiGenerated = objectiveType === OBJECTIVE_TYPE_AI_GENERATED;
  const isMultipleChoice = objectiveType === OBJECTIVE_TYPE_MULTIPLE_CHOICE;
  const [name, setName] = useState<string>(
    props.objective?.name || ConstantValues.EMPTY_STRING,
  );
  const [description, setDescription] = useState<string>(
    props.objective?.description || ConstantValues.EMPTY_STRING,
  );
  const [maxQuestions, setMaxQuestions] = useState<number | undefined>(
    props.objective?.maxQuestions,
  );
  const [sampleQuestions, setSampleQuestions] = useState<IObjectiveQuestion[]>(
    (props.objective?.sampleQuestions || []).map((question) => ({
      ...question,
      id: crypto.randomUUID(),
    })),
  );
  const [selectOptions, setSelectOptions] = useState<Record<string, string>>(
    props.objective?.selectOptions ?? {
      ...(isLikert ? DEFAULT_LIKERT_SCALE_LABELS : {}),
    },
  );

  const nameRef = useRef<HTMLInputElement>(null);
  const [allowSkipping, setAllowSkipping] = useState<boolean>(
    props.objective?.allowSkipping !== undefined
      ? props.objective?.allowSkipping
      : true,
  );
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const maxQuestionsRef = useRef<HTMLInputElement>(null);
  const starterQuestionRef = useRef<HTMLInputElement>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<"details" | "generate">("details");
  const [generatedOutput, setGeneratedOutput] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [captureInput, setCaptureInput] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );

  const addSample = () =>
    setSampleQuestions((prev) => [
      ...prev,
      { text: ConstantValues.EMPTY_STRING, required: false },
    ]);

  const removeSample = (idx: number) =>
    setSampleQuestions((prev) => prev.filter((_, i) => i !== idx));

  const updateSample = (idx: number, patch: Partial<IObjectiveQuestion>) =>
    setSampleQuestions((prev) =>
      prev.map((q, i) => (i === idx ? { ...q, ...patch } : q)),
    );

  const getAnswersErrorKey = (questionId: string) => `answers-${questionId}`;

  const addMultipleChoiceQuestion = () =>
    setSampleQuestions((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        text: ConstantValues.EMPTY_STRING,
        required: true,
        options: [],
        allowMultipleSelection: true,
      },
    ]);

  const moveQuestion = (idx: number, direction: -1 | 1) =>
    setSampleQuestions((prev) => {
      const target = idx + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });

  const updateQuestionOptions = (
    idx: number,
    update: (options: string[]) => string[],
  ) => {
    updateSample(idx, {
      options: update(sampleQuestions[idx]?.options ?? []),
    });
    const questionId = sampleQuestions[idx]?.id;
    setFieldErrors((prev) => {
      const { questions: _, ...rest } = prev;
      if (!questionId) return rest;
      const { [getAnswersErrorKey(questionId)]: __, ...remaining } = rest;
      return remaining;
    });
  };

  const addOption = (idx: number) =>
    updateQuestionOptions(idx, (options) => [
      ...options,
      ConstantValues.EMPTY_STRING,
    ]);

  const removeOption = (idx: number, optionIndex: number) =>
    updateQuestionOptions(idx, (options) =>
      options.filter((_, i) => i !== optionIndex),
    );

  const updateOption = (idx: number, optionIndex: number, value: string) =>
    updateQuestionOptions(idx, (options) =>
      options.map((option, i) => (i === optionIndex ? value : option)),
    );

  const moveSample = (idx: number, direction: -1 | 1) =>
    setSampleQuestions((prev) => {
      const target = idx + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });

  const moveScaleLabel = (labelKey: string, direction: -1 | 1) =>
    setSelectOptions((prev) => {
      const neighborKey = String(Number(labelKey) + direction);
      if (!(neighborKey in prev)) return prev;
      return {
        ...prev,
        [labelKey]: prev[neighborKey],
        [neighborKey]: prev[labelKey],
      };
    });

  const setStarterQuestion = (text: string) =>
    setSampleQuestions(text === "" ? [] : [{ text, required: true }]);

  const handleGenerate = async () => {
    setIsGenerating(true);
    setFieldErrors((prev) => {
      const { generate: _, ...rest } = prev;
      return rest;
    });
    try {
      const result = await generateObjectiveDescription({
        name,
        description,
        captureInput,
      });
      setGeneratedOutput(result.description);
    } catch {
      setGeneratedOutput(ConstantValues.EMPTY_STRING);
      setFieldErrors((prev) => ({
        ...prev,
        generate: "Failed to generate description. Please try again.",
      }));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyToDescription = () => {
    setDescription(generatedOutput);
    setActiveTab("details");
  };

  const handleTextChange =
    (setter: (val: string) => void, fieldKey: string) =>
    (_: ChangeEvent<HTMLInputElement>, data: { value: string }) => {
      setter(data.value);
      setFieldErrors((prev) => {
        const { [fieldKey]: _, ...rest } = prev;
        return rest;
      });
    };

  const validateFields = (): boolean => {
    const errors: Record<string, string> = {};

    if (!name.trim()) {
      errors.name = "Name is required.";
    }

    switch (objectiveType) {
      case OBJECTIVE_TYPE_PREDEFINED: {
        const filled = sampleQuestions.filter((q) => q.text.trim());
        if (
          filled.length < MIN_PREDEFINED_QUESTIONS ||
          filled.length > MAX_PREDEFINED_QUESTIONS
        ) {
          errors.questions = `Add ${MIN_PREDEFINED_QUESTIONS}-${MAX_PREDEFINED_QUESTIONS} questions.`;
        } else if (
          filled.some(
            (q) => q.text.trim().length > MAX_OBJECTIVE_QUESTION_LENGTH,
          )
        ) {
          errors.questions = `Each question must be at most ${MAX_OBJECTIVE_QUESTION_LENGTH} characters.`;
        }
        break;
      }
      case OBJECTIVE_TYPE_LIKERT_SCALE: {
        const filled = sampleQuestions.filter((s) => s.text.trim());
        if (
          filled.length < MIN_LIKERT_STATEMENTS ||
          filled.length > MAX_LIKERT_STATEMENTS
        ) {
          errors.statements = `Add ${MIN_LIKERT_STATEMENTS}-${MAX_LIKERT_STATEMENTS} statements.`;
        } else if (
          filled.some(
            (s) => s.text.trim().length > MAX_OBJECTIVE_QUESTION_LENGTH,
          )
        ) {
          errors.statements = `Each statement must be at most ${MAX_OBJECTIVE_QUESTION_LENGTH} characters.`;
        }

        if (Object.values(selectOptions).some((label) => !label?.trim())) {
          errors.scaleLabels = "All scale labels (1-5) are required.";
        } else if (
          Object.values(selectOptions).some(
            (label) => label.trim().length > MAX_LIKERT_SCALE_LABEL_LENGTH,
          )
        ) {
          errors.scaleLabels = `Each scale label must be at most ${MAX_LIKERT_SCALE_LABEL_LENGTH} characters.`;
        }
        break;
      }
      case OBJECTIVE_TYPE_MULTIPLE_CHOICE: {
        const filled = sampleQuestions.filter((q) => q.text.trim());
        if (
          filled.length < MIN_MULTIPLE_CHOICE_QUESTIONS ||
          filled.length > MAX_MULTIPLE_CHOICE_QUESTIONS
        ) {
          errors.questions = `Add ${MIN_MULTIPLE_CHOICE_QUESTIONS}-${MAX_MULTIPLE_CHOICE_QUESTIONS} questions.`;
        } else if (
          filled.some(
            (q) => q.text.trim().length > MAX_OBJECTIVE_QUESTION_LENGTH,
          )
        ) {
          errors.questions = `Each question must be at most ${MAX_OBJECTIVE_QUESTION_LENGTH} characters.`;
        }

        filled.forEach((q) => {
          if (!q.id) return;
          const options = q.options ?? [];
          if (options.length < MIN_MULTIPLE_CHOICE_OPTIONS) {
            errors[getAnswersErrorKey(q.id)] =
              `Add at least ${MIN_MULTIPLE_CHOICE_OPTIONS} answer.`;
          } else if (options.some((option) => !option.trim())) {
            errors[getAnswersErrorKey(q.id)] = "All answers must have a value.";
          } else if (
            options.some(
              (option) =>
                option.trim().length > MAX_MULTIPLE_CHOICE_OPTION_LENGTH,
            )
          ) {
            errors[getAnswersErrorKey(q.id)] =
              `Each answer must be at most ${MAX_MULTIPLE_CHOICE_OPTION_LENGTH} characters.`;
          }
        });
        break;
      }
      case OBJECTIVE_TYPE_AI_GENERATED: {
        if (!description.trim()) {
          errors.description = "Description is required.";
        }

        if (maxQuestions === undefined) {
          errors.maxQuestions = "Number of questions is required.";
        } else if (isNaN(maxQuestions) || maxQuestions <= 0) {
          errors.maxQuestions = "Must be a positive number.";
        }

        if (!sampleQuestions[0]?.text.trim()) {
          errors.starterQuestion = "Starter question is required.";
        }
        break;
      }
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      if (errors.name) nameRef.current?.focus();
      else if (errors.description) descriptionRef.current?.focus();
      else if (errors.maxQuestions) maxQuestionsRef.current?.focus();
      else if (errors.starterQuestion) starterQuestionRef.current?.focus();
    }
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (): Promise<void> => {
    if (!validateFields()) return;
    setIsLoading(true);

    try {
      const cleanQuestions = sampleQuestions
        .filter((q) => q.text.trim())
        .map((q) => ({
          text: q.text.trim(),
          required:
            objectiveType === OBJECTIVE_TYPE_PREDEFINED ||
            objectiveType === OBJECTIVE_TYPE_MULTIPLE_CHOICE ||
            objectiveType === OBJECTIVE_TYPE_AI_GENERATED ||
            q.required,
          ...(isMultipleChoice && {
            options: (q.options ?? []).map((option) => option.trim()),
            allowMultipleSelection: q.allowMultipleSelection ?? true,
          }),
        }));

      const trimmedScaleLabels = Object.fromEntries(
        Object.entries(selectOptions).map(([key, value]) => [
          key,
          value.trim(),
        ]),
      );

      let body: Record<string, unknown>;
      switch (objectiveType) {
        case OBJECTIVE_TYPE_PREDEFINED:
          body = {
            name: name || undefined,
            description: undefined,
            text: cleanQuestions.map((q) => q.text).join("\n\n"),
            sampleQuestions: cleanQuestions,
            maxQuestions: cleanQuestions.length,
            objectiveType: OBJECTIVE_TYPE_PREDEFINED,
            allowSkipping,
          };
          break;
        case OBJECTIVE_TYPE_LIKERT_SCALE:
          body = {
            name: name || undefined,
            description: undefined,
            text: cleanQuestions.map((q) => q.text).join("\n\n"),
            sampleQuestions: cleanQuestions,
            selectOptions: trimmedScaleLabels,
            objectiveType: OBJECTIVE_TYPE_LIKERT_SCALE,
            allowSkipping,
          };
          break;
        case OBJECTIVE_TYPE_MULTIPLE_CHOICE:
          body = {
            name: name || undefined,
            description: undefined,
            text: cleanQuestions.map((q) => q.text).join("\n\n"),
            sampleQuestions: cleanQuestions,
            objectiveType: OBJECTIVE_TYPE_MULTIPLE_CHOICE,
            allowSkipping,
          };
          break;
        default:
          body = {
            name: name || undefined,
            description: description,
            text: description,
            sampleQuestions: cleanQuestions,
            maxQuestions: maxQuestions ?? undefined,
            objectiveType: OBJECTIVE_TYPE_AI_GENERATED,
            allowSkipping,
          };
      }
      let saved: IObjective;

      if (isEditMode && props.objective) {
        saved = await updateObjective({
          id: props.diagnosticId,
          questionNumber: props.objective.questionNumber,
          body,
        });
      } else {
        saved = await addObjectiveToDiagnostic({
          id: props.diagnosticId,
          body,
        });

        await addObjectiveCreated({
          objectiveId: saved._id?.toString() || ConstantValues.EMPTY_STRING,
        });
      }

      props.onDismiss(saved);
    } catch (error) {
      console.error("Failed to save objective:", error);
      setFieldErrors({ general: "An error occurred while saving." });
    } finally {
      setIsLoading(false);
    }
  };

  const filledQuestionsCount = sampleQuestions.filter((q) =>
    q.text.trim(),
  ).length;
  const allScaleLabelsComplete = Object.values(selectOptions).every((label) =>
    Boolean(label?.trim()),
  );
  const hasIncompleteDetails = (() => {
    switch (objectiveType) {
      case OBJECTIVE_TYPE_PREDEFINED:
        return (
          !name.trim() ||
          filledQuestionsCount < MIN_PREDEFINED_QUESTIONS ||
          filledQuestionsCount > MAX_PREDEFINED_QUESTIONS
        );
      case OBJECTIVE_TYPE_LIKERT_SCALE:
        return (
          !name.trim() ||
          filledQuestionsCount < MIN_LIKERT_STATEMENTS ||
          filledQuestionsCount > MAX_LIKERT_STATEMENTS ||
          !allScaleLabelsComplete
        );
      case OBJECTIVE_TYPE_MULTIPLE_CHOICE:
        return (
          !name.trim() ||
          filledQuestionsCount < MIN_MULTIPLE_CHOICE_QUESTIONS ||
          filledQuestionsCount > MAX_MULTIPLE_CHOICE_QUESTIONS ||
          sampleQuestions.some(
            (q) =>
              q.text.trim() &&
              ((q.options ?? []).length < MIN_MULTIPLE_CHOICE_OPTIONS ||
                (q.options ?? []).some((option) => !option.trim())),
          )
        );
      case OBJECTIVE_TYPE_AI_GENERATED:
        return (
          !name.trim() ||
          !description.trim() ||
          maxQuestions === undefined ||
          !sampleQuestions[0]?.text.trim()
        );
      default:
        return false;
    }
  })();

  return (
    <Dialog
      open={props.isOpen}
      onOpenChange={(_, data) => {
        if (!data.open) props.onDismiss();
      }}
    >
      <DialogSurface
        className={mergeClasses(
          editStyles.dialogSurface,
          footerStyles.dialogSurface,
        )}
      >
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label="Close"
                disabled={isLoading}
                onClick={() => props.onDismiss()}
              />
            }
          >
            <span className={editStyles.dialogTitleContent}>
              {isEditMode ? "Update Objective Details" : "Create Objective"}
              <Tooltip
                content="Objectives are learning goals inside the diagnostic; each generates questions."
                relationship="description"
                withArrow
              >
                <Info16Regular
                  aria-label="Objectives info"
                  className={editStyles.dialogTitleInfoIcon}
                />
              </Tooltip>
            </span>
          </DialogTitle>
          <DialogContent className={editStyles.dialogContent}>
            <DialogTabs
              tabs={
                isAiGenerated
                  ? [
                      { key: "details", label: "Details" },
                      {
                        key: "generate",
                        label: "Generate Objective Description",
                      },
                    ]
                  : [{ key: "details", label: "Details" }]
              }
              activeTab={activeTab}
              onTabClick={(key) =>
                !isLoading && setActiveTab(key as "details" | "generate")
              }
            />

            {activeTab === "details" && (
              <>
                <div className={editStyles.contentFrame}>
                  {isLoading && (
                    <div className={dialogContentStyles.formLoadingOverlay}>
                      <Spinner label="Saving objective..." />
                    </div>
                  )}
                  <div className={editStyles.contentWrapper}>
                    <Field
                      label={
                        <LabelWithInfo
                          label="Name"
                          info="Will be visible to users during the interview."
                          required
                        />
                      }
                      validationState={fieldErrors.name ? "error" : "none"}
                      validationMessage={fieldErrors.name}
                    >
                      <Input
                        ref={nameRef}
                        value={name}
                        disabled={isLoading}
                        onChange={handleTextChange(setName, "name")}
                        placeholder="Enter objective name..."
                      />
                    </Field>
                    <Field
                      label={
                        <LabelWithInfo
                          label="Objective Type"
                          info="Choose how questions are produced for this objective. Once created, the objective type cannot be changed."
                          required
                        />
                      }
                      className={editStyles.typeSelectorField}
                    >
                      <RadioGroup
                        layout="vertical"
                        value={objectiveType}
                        disabled={isEditMode || isLoading}
                        onChange={(_, data) => {
                          if (
                            data.value === OBJECTIVE_TYPE_PREDEFINED &&
                            !isPredefined &&
                            description.trim()
                          ) {
                            const proceed = window.confirm(
                              "Switching to Predefined will discard your description. Continue?",
                            );
                            if (!proceed) return;
                          }
                          setObjectiveType(data.value as ObjectiveType);
                          setFieldErrors({});
                          setDescription(ConstantValues.EMPTY_STRING);
                          setMaxQuestions(undefined);
                          setSampleQuestions([]);
                          setSelectOptions({ ...DEFAULT_LIKERT_SCALE_LABELS });
                          setActiveTab("details");
                        }}
                        aria-label="Objective type"
                      >
                        <Tooltip
                          content={
                            isEditMode
                              ? "Objective type cannot be changed after creation."
                              : "AI generates the interview questions automatically from your objective description."
                          }
                          relationship="description"
                          positioning={{
                            position: "after",
                            align: "center",
                            offset: 4,
                          }}
                          withArrow
                        >
                          <span className={editStyles.radioTooltipTrigger}>
                            <Radio
                              value={OBJECTIVE_TYPE_AI_GENERATED}
                              label="AI Generated Questions"
                            />
                          </span>
                        </Tooltip>
                        <Tooltip
                          content={
                            isEditMode
                              ? "Objective type cannot be changed after creation."
                              : "You provide 1–3 static questions that are asked exactly as written, with no AI generation."
                          }
                          relationship="description"
                          positioning={{
                            position: "after",
                            align: "center",
                            offset: 4,
                          }}
                          withArrow
                        >
                          <span className={editStyles.radioTooltipTrigger}>
                            <Radio
                              value={OBJECTIVE_TYPE_PREDEFINED}
                              label="Predefined Questions"
                            />
                          </span>
                        </Tooltip>
                        <Tooltip
                          content={
                            isEditMode
                              ? "Objective type cannot be changed after creation."
                              : "Respondents rate each statement on a fixed scale — Strongly Disagree through Strongly Agree, plus N/A. The scale can't be changed. The AI won't rephrase statements or ask follow-up questions in this section."
                          }
                          relationship="description"
                          positioning={{
                            position: "after",
                            align: "center",
                            offset: 4,
                          }}
                          withArrow
                        >
                          <span className={editStyles.radioTooltipTrigger}>
                            <Radio
                              value={OBJECTIVE_TYPE_LIKERT_SCALE}
                              label={OBJECTIVE_TYPE_LABEL_LIKERT_SCALE}
                            />
                          </span>
                        </Tooltip>
                        <Tooltip
                          content={
                            isEditMode
                              ? "Objective type cannot be changed after creation."
                              : "Respondents choose from the answer options you write for each question, and can select more than one. Options are set per question. The AI won't rephrase your questions or options, or ask follow-ups in this section."
                          }
                          relationship="description"
                          positioning={{
                            position: "after",
                            align: "center",
                            offset: 4,
                          }}
                          withArrow
                        >
                          <span className={editStyles.radioTooltipTrigger}>
                            <Radio
                              value={OBJECTIVE_TYPE_MULTIPLE_CHOICE}
                              label={OBJECTIVE_TYPE_LABEL_MULTIPLE_CHOICE}
                            />
                          </span>
                        </Tooltip>
                      </RadioGroup>
                    </Field>
                    <CustomToggle
                      checked={allowSkipping}
                      onChange={(allow: boolean) =>
                        setAllowSkipping(allow.valueOf())
                      }
                      label="Allow the interviewee to skip the objective"
                      isLabelBold={false}
                      disabled={isLoading}
                    />
                    {isPredefined && (
                      <PredefinedQuestionsForm
                        sampleQuestions={sampleQuestions}
                        isLoading={isLoading}
                        fieldErrors={fieldErrors}
                        onAddQuestion={addSample}
                        onRemoveQuestion={removeSample}
                        onUpdateQuestion={updateSample}
                        maxQuestionsCount={MAX_PREDEFINED_QUESTIONS}
                        editStyles={editStyles}
                        dialogContentStyles={dialogContentStyles}
                      />
                    )}
                    {isLikert && (
                      <LikertScaleForm
                        likertStatements={sampleQuestions}
                        likertScaleLabels={selectOptions}
                        isLoading={isLoading}
                        fieldErrors={fieldErrors}
                        onAddStatement={addSample}
                        onRemoveStatement={removeSample}
                        onUpdateStatement={updateSample}
                        onMoveStatement={moveSample}
                        onMoveScaleLabel={moveScaleLabel}
                        onUpdateScaleLabel={(
                          labelKey: string,
                          value: string,
                        ) => {
                          setSelectOptions((prev) => ({
                            ...prev,
                            [labelKey]: value,
                          }));
                          setFieldErrors((prevErrors) => {
                            const { scaleLabels: _, ...rest } = prevErrors;
                            return rest;
                          });
                        }}
                        maxStatementsCount={MAX_LIKERT_STATEMENTS}
                        editStyles={editStyles}
                        dialogContentStyles={dialogContentStyles}
                      />
                    )}
                    {isMultipleChoice && (
                      <MultipleChoiceForm
                        questions={sampleQuestions}
                        isLoading={isLoading}
                        fieldErrors={fieldErrors}
                        onAddQuestion={addMultipleChoiceQuestion}
                        onRemoveQuestion={removeSample}
                        onUpdateQuestion={updateSample}
                        onMoveQuestion={moveQuestion}
                        onAddOption={addOption}
                        onRemoveOption={removeOption}
                        onUpdateOption={updateOption}
                        maxQuestionsCount={MAX_MULTIPLE_CHOICE_QUESTIONS}
                        maxOptionsCount={MAX_MULTIPLE_CHOICE_OPTIONS}
                        editStyles={editStyles}
                        dialogContentStyles={dialogContentStyles}
                      />
                    )}
                    {isAiGenerated && (
                      <AiGeneratedQuestionsForm
                        description={description}
                        maxQuestions={maxQuestions}
                        starterQuestion={sampleQuestions[0]?.text ?? ""}
                        isLoading={isLoading}
                        fieldErrors={fieldErrors}
                        descriptionRef={descriptionRef}
                        maxQuestionsRef={maxQuestionsRef}
                        starterQuestionRef={starterQuestionRef}
                        onDescriptionChange={(value: string) => {
                          setDescription(value);
                          setFieldErrors((prev) => {
                            const { description: _, ...rest } = prev;
                            return rest;
                          });
                        }}
                        onMaxQuestionsChange={(value: number | undefined) => {
                          setMaxQuestions(value);
                          setFieldErrors((prev) => {
                            const { maxQuestions: _, ...rest } = prev;
                            return rest;
                          });
                        }}
                        onStarterQuestionChange={(value: string) => {
                          setStarterQuestion(value);
                        }}
                        editStyles={editStyles}
                        dialogContentStyles={dialogContentStyles}
                        setActiveTab={setActiveTab}
                      />
                    )}

                    {fieldErrors.general && (
                      <div className={dialogContentStyles.feedbackError}>
                        {fieldErrors.general}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {activeTab === "generate" && !isPredefined && (
              <div className={editStyles.generateTab}>
                <Text size={300}>
                  Use this form to have AI help you create your objective
                  description.
                </Text>

                <Field label="What do you want to capture from the candidate?">
                  <div className={editStyles.textareaWithFooter}>
                    <Textarea
                      value={captureInput}
                      rows={7}
                      onChange={(_, data) => setCaptureInput(data.value)}
                      disabled={isGenerating}
                      placeholder="E.g. candidate's experience with React, problem-solving approach, etc."
                      className={editStyles.textareaNoBottomRadius}
                    />
                    <div className={editStyles.inputFooter}>
                      <Tooltip
                        content={
                          isGenerating
                            ? "Generating..."
                            : "Generate description"
                        }
                        relationship="label"
                        withArrow
                      >
                        <Button
                          appearance="subtle"
                          icon={
                            isGenerating ? (
                              <Spinner size="tiny" />
                            ) : (
                              <SparkleRegular />
                            )
                          }
                          onClick={handleGenerate}
                          disabled={!captureInput.trim() || isGenerating}
                          aria-label={
                            isGenerating
                              ? "Generating description, please wait"
                              : "Generate description"
                          }
                          aria-busy={isGenerating}
                        />
                      </Tooltip>
                    </div>
                  </div>
                </Field>

                <Field label="Generated output">
                  <div className={editStyles.textareaWithFooter}>
                    <div className={editStyles.generateOutput}>
                      {generatedOutput !== ConstantValues.EMPTY_STRING ? (
                        <Text size={300}>{generatedOutput}</Text>
                      ) : (
                        <Text
                          size={300}
                          className={editStyles.generateOutputPlaceholder}
                        >
                          No output generated yet.
                        </Text>
                      )}
                    </div>
                    <div className={editStyles.inputFooter}>
                      <Tooltip
                        content="Copy to description"
                        relationship="label"
                        withArrow
                      >
                        <Button
                          appearance="subtle"
                          icon={<ArrowEnterRegular />}
                          onClick={handleCopyToDescription}
                          disabled={
                            generatedOutput === ConstantValues.EMPTY_STRING
                          }
                          aria-label="Copy to description"
                        />
                      </Tooltip>
                    </div>
                  </div>
                </Field>

                {fieldErrors.generate && (
                  <div className={dialogContentStyles.feedbackError}>
                    {fieldErrors.generate}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </DialogBody>
        {activeTab === "details" && (
          <div className={footerStyles.dialogFooter}>
            <DialogFooterAction
              icon={<CheckmarkRegular />}
              onClick={handleSave}
              disabled={isLoading}
              dimmed={hasIncompleteDetails || isLoading}
              tooltip="Save"
              ariaLabel="Save objective"
            />
          </div>
        )}
      </DialogSurface>
    </Dialog>
  );
};
